/**
 * @file fields.tsx
 * @description Поля форм кабинета сайта (phase19.8): подписанное поле и
 * картинка с загрузкой в content-media (страница «Свидетельства»).
 * phase19.16 Р2 «чистка» (TD-327): поле видео/аудио и редактор списка
 * прежних блоков удалены вместе с их формами.
 *
 * Загруженные картинки сохраняются в payload как стабильный публичный путь
 * `/api/v1/public/content/media/<id>` — файл отдаёт сам API (с v0.19.43;
 * раньше был 302 на presigned MinIO): в payload не попадают протухающие
 * presigned-URL.
 */

import { ImageIcon, ImagePlus } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";

import { ApiError } from "../../lib/api-client";
import { uploadContentMedia } from "../../lib/api-content-media";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useNotify } from "../ui/NotifyProvider";
import { ImageCropper } from "./ImageCropper";
import { resolveWebAssetUrl, usePreviewConfig } from "./preview-config-context";

/** Подписанное поле формы блока. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-on-surface-muted">{label}</span>
      {children}
    </label>
  );
}

/** Стабильный публичный путь медиа-ассета (реюз content-media, phase19.8). */
export function contentMediaPath(assetId: string): string {
  return `/api/v1/public/content/media/${assetId}`;
}

/**
 * Поле URL картинки (phase19.8.2): превью ТЕКУЩЕГО фото + ручной ввод пути
 * (`/images/...`) + загрузка файла с inline-кропом (react-easy-crop) —
 * в поле пишется стабильный публичный путь content-media.
 */
export function ImageUrlField({
  label,
  value,
  onChange,
  hintAspect,
  hintRange,
}: {
  label: string;
  value: string;
  /**
   * phase19.8.3 — вместе с URL отдаём пропорции кадра (width/height):
   * блок подстраивает под них свою высоту. `undefined` — не удалось измерить.
   */
  onChange: (url: string, aspect?: number) => void;
  /** Типичный формат блока — подсветка пресета в кропере (не ограничение). */
  hintAspect?: number;
  /** Коридор блока: за его пределами кадр обрежется (предупреждение). */
  hintRange?: { min: number; max: number };
}) {
  const notify = useNotify();
  const previewConfig = usePreviewConfig();
  const fileRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const previewUrl = resolveWebAssetUrl(value, previewConfig);

  async function onUpload(file: File, aspect: number) {
    setUploading(true);
    try {
      const asset = await uploadContentMedia(file, "");
      onChange(contentMediaPath(asset.id), aspect);
      setPendingFile(null);
      notify.success("Картинка загружена");
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось загрузить");
    } finally {
      setUploading(false);
    }
  }

  /** Ручной ввод пути: пропорции измеряем по загруженной картинке. */
  function onManualUrl(url: string): void {
    onChange(url);
    const absolute = resolveWebAssetUrl(url, previewConfig);
    if (!absolute) return;
    const img = new Image();
    img.onload = () => {
      if (img.naturalHeight > 0) onChange(url, img.naturalWidth / img.naturalHeight);
    };
    img.src = absolute;
  }

  return (
    <Field label={label}>
      <div className="flex flex-col gap-2">
        {pendingFile === null ? (
          <div className="flex items-start gap-2">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Текущее фото"
                className="h-16 w-24 shrink-0 rounded object-cover"
                loading="lazy"
              />
            ) : (
              <div className="flex h-16 w-24 shrink-0 items-center justify-center rounded bg-surface-low">
                <ImageIcon className="h-5 w-5 text-on-surface-muted" />
              </div>
            )}
            <div className="flex flex-1 gap-2">
              <Input
                value={value}
                onChange={(e) => onManualUrl(e.target.value)}
                placeholder="/images/… или загрузите файл"
              />
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setPendingFile(f);
                  e.target.value = "";
                }}
              />
              <Button
                type="button"
                variant="outline"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                aria-label="Загрузить картинку"
              >
                <ImagePlus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <ImageCropper
            file={pendingFile}
            busy={uploading}
            hintAspect={hintAspect}
            hintRange={hintRange}
            onCancel={() => setPendingFile(null)}
            onDone={(file, aspect) => void onUpload(file, aspect)}
          />
        )}
      </div>
    </Field>
  );
}
